import { Skeleton } from '@growdash/design-system/atoms';
import {
  MenuItem,
  PaginatedResponseMenuItem,
} from '@growdash/growintel/generated';
import { useGrowintelService } from '@growdash/growintel/hooks/useGrowintelService';
import sortByField from '@growdash/growintel/utils/sort-by-field';
import { useQuery } from '@tanstack/react-query';
import MenuItemCard from './MenuItemCard';

type Props = {
  branchId: string | undefined;
};

export default function MenuItemsView({ branchId }: Props) {
  const service = useGrowintelService();
  const { data: menuData, isFetching: menuLoading } = useQuery({
    queryKey: ['restaurant-menu', branchId],
    queryFn: async (): Promise<PaginatedResponseMenuItem | undefined> => {
      if (!branchId) return;
      const response =
        await service?.menuItemsAPI.getMenuItemsByBranchMenuItemsByBranchGet(
          branchId,
          1,
          999999,
        );

      return response?.data;
    },
  });

  const groupItems = (items: MenuItem[]) =>
    items.reduce(
      (
        acc: {
          [key: string]: MenuItem[];
        },
        item: MenuItem,
      ) => {
        const key = item.item_category || 'Uncategorized';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      },
      {},
    );

  return menuLoading ? (
    <div className="w-full grid grid-cols-4 gap-4 gap-y-4 animate-fade-in">
      {Array.from({ length: 16 }).map((_, index) => (
        <Skeleton
          key={index}
          className="h-32 rounded-lg overflow-hidden"
          style={{ width: '100%' }}
        />
      ))}
    </div>
  ) : !menuData?.items?.length ? (
    <div className="flex w-full justify-center items-center h-96">
      <p className="text-muted-foreground">No menu item found</p>
    </div>
  ) : (
    Object.entries(
      groupItems(sortByField(menuData?.items!, 'item_category')),
    ).map(([category, items]) => (
      <div className="mb-8" key={category}>
        <div className="text-xl font-semibold mb-2 flex items-center gap-1">
          {category}
        </div>
        <div className="w-full grid grid-cols-4 gap-4 gap-y-4 animate-fade-in">
          {items.map((item) => (
            <MenuItemCard {...item} key={item.item_id} />
          ))}
        </div>
      </div>
    ))
  );
}
